import { useMemo } from "react";

import { Chip } from "@/components/ui/Chip";
import { LinkIcon } from "@/components/ui/Icons";
import { Link } from "@/components/ui/Link";
import { Tooltip } from "@/components/ui/Tooltip";
import { Column, createChipColumn, createDataColumn } from "@/components/ui/table-final-saviour/Table";
import { ExpandedContent } from "@/components/ui/table-final-saviour/Table/TableExpandable";
import { EnrichedInsight } from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/action-list.types";
import {
    getInsightSeverityColor,
    insightCategoryDisplayNameMap,
    milkingPageRouteLookup,
} from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/action-list.utils";
import { routes } from "@/routes";
import { generatePath } from "@/routes/routes.utils";
import { CustomerInsightDetail } from "@/types/insights";

type Props = {
    row: EnrichedInsight;
};

export const ActionListTableExpandedContent = ({ row }: Props) => {
    const { customerNumber, insights } = row;

    const dataSource = {
        data: insights,
    };

    // Sort alphabetically by category
    const sortedData = useMemo(
        () => dataSource.data.toSorted((a, b) => a.category.localeCompare(b.category)),
        [dataSource.data],
    );

    const columns = useMemo(
        () => [
            createDataColumn({
                title: "Category",
                dataKey: "category",
                dataType: "string",
                width: 200,
                getCellValue: ({ row }) => {
                    return insightCategoryDisplayNameMap[row.category] ?? row.category;
                },
            }),
            createChipColumn({
                title: "Insight",
                width: 200,
                getChip: ({ row }) => {
                    const linkTo = generatePath(routes.customerDetail, {
                        subRoute: routes.customerDetail.subRoutes.milking,
                        subSubRoute: milkingPageRouteLookup[row.category],
                        params: {
                            customerNumber,
                        },
                    });

                    const displayName = row.data.displayName ?? row.type;
                    const tooltipContent = row.data.fullDisplayName ?? displayName;
                    const content = (
                        <Tooltip content={<>{tooltipContent}</>}>
                            <Chip
                                variant={"outlined"}
                                color={getInsightSeverityColor(row.severity)}
                                endIcon={<LinkIcon />}
                            >
                                <>{displayName}</>
                            </Chip>
                        </Tooltip>
                    );

                    return linkTo ? <Link to={linkTo}>{content}</Link> : content;
                },
            }),
            createDataColumn({
                title: "Description",
                dataType: "string",
                dataKey: "data",
                getCellValue: ({ row }) => {
                    return String(row.data.description ?? "None");
                },
            }),
        ],
        [customerNumber],
    ) as Column<CustomerInsightDetail>[];

    return (
        <ExpandedContent>
            <ExpandedContent.Section>
                <ExpandedContent.Section.Table
                    columns={columns}
                    dataSource={{
                        ...dataSource,
                        data: sortedData,
                    }}
                    maxHeight={400}
                />
            </ExpandedContent.Section>
        </ExpandedContent>
    );
};
